import React, { useContext, useMemo, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  EmptyState,
} from "@jem-open/jem-ui";
import { Download } from "lucide-react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Space } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import PageContainer from "../../../themes/components/PageContainer";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import NotFound from "../../../common/pages/NotFound";
import { AuthContext } from "../../../providers/AuthProvider";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import UserAccessControlUtils, {
  USER_ROLE_MAPPING,
  FEATURE_INTEGRATIONS,
} from "../../../utils/UserAccessControlUtils";
import useEmployeeAuditLogApiService, {
  EmployeeAuditLogApiService,
} from "../services/api/EmployeeAuditLogApiService";
import { EmployeeAuditLogResponse } from "../services/model/EmployeeAuditEntry";
import EmployeeAuditLogTable from "../components/EmployeeAuditLogTable";
import EmployeeAuditLogFilters, {
  AuditLogDateRange,
  AuditLogFilterState,
  ALL_FILTER_VALUE,
} from "../components/EmployeeAuditLogFilters";
import {
  downloadAuditLogCSV,
  generateAuditLogCSV,
} from "../utils/employeeAuditLogUtils";

const PAGE_SIZE = 10;

const buildDefaultDateRange = (): AuditLogDateRange => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from, to };
};

const buildInitialFilters = (): AuditLogFilterState => ({
  dateRange: buildDefaultDateRange(),
  field: ALL_FILTER_VALUE,
  source: ALL_FILTER_VALUE,
});

const EmployeeAuditLogPage: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissions } = useContext(AccessPermissionContext);
  const { employeeId } = useParams();

  const employeeName = (location.state as { employeeName?: string } | null)
    ?.employeeName;

  const [filters, setFilters] =
    useState<AuditLogFilterState>(buildInitialFilters);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const requestParams = useMemo(
    () => ({
      employee_id: employeeId ?? "",
      page,
      page_size: PAGE_SIZE,
      date_from: filters.dateRange?.from?.toISOString(),
      date_to: filters.dateRange?.to?.toISOString(),
      fields: filters.field !== ALL_FILTER_VALUE ? [filters.field] : undefined,
      sources:
        filters.source !== ALL_FILTER_VALUE
          ? [filters.source as "system" | "manual" | "integration"]
          : undefined,
    }),
    [employeeId, page, filters]
  );

  const { isLoading, isError, error, data, isFetching } =
    useEmployeeAuditLogApiService(requestParams);

  const isIntegrationsEnabled = UserAccessControlUtils.hasFeature(
    auth?.user_features ?? [],
    FEATURE_INTEGRATIONS
  );

  if (!auth) return <Navigate replace to="/login" />;
  if (
    !hasPermissions ||
    auth.user_role !== USER_ROLE_MAPPING.ADMIN ||
    !isIntegrationsEnabled
  )
    return <NotFound />;
  if (!employeeId) return <Navigate replace to="/employees" />;

  const renderContent = (): React.ReactNode => {
    if (isLoading) return <ProgressIndicator />;
    if (isError) return <ErrorPage errors={error} />;
    if (!data?.results.length) {
      return (
        <EmptyState
          variant="bordered"
          size="sm"
          icon="search"
          title="No changes found"
          description="No audit entries match the current filters."
          className="!max-w-none w-full !py-lg [&_.bg-secondary-pink-50]:p-1"
        />
      );
    }
    return (
      <div style={{ opacity: isFetching ? 0.6 : 1 }}>
        <EmployeeAuditLogTable entries={data.results} />

        {data.pages > 1 && (
          <div className="flex items-center justify-center gap-xs mt-md">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-greyscale-text-caption">
              Page {page} of {data.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.pages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    );
  };

  const handleFilterChange = (next: AuditLogFilterState): void => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = (): void => {
    setFilters(buildInitialFilters());
    setPage(1);
  };

  const handleDownloadCsv = async (): Promise<void> => {
    setIsExporting(true);
    try {
      const first = await EmployeeAuditLogApiService({
        ...requestParams,
        page: 1,
        page_size: 100,
      });
      let allEntries = [...first.results];

      if (first.pages > 1) {
        const batchSize = 5;
        const pageNumbers = Array.from(
          { length: first.pages - 1 },
          (_, i) => i + 2
        );
        const batches: number[][] = [];
        for (let i = 0; i < pageNumbers.length; i += batchSize) {
          batches.push(pageNumbers.slice(i, i + batchSize));
        }

        const fetchBatch = (
          pages: number[]
        ): Promise<EmployeeAuditLogResponse[]> =>
          Promise.all(
            pages.map((pg) =>
              EmployeeAuditLogApiService({
                ...requestParams,
                page: pg,
                page_size: 100,
              })
            )
          );

        const batchResults = await batches.reduce(async (prev, batch) => {
          const acc = await prev;
          const results = await fetchBatch(batch);
          return acc.concat(results);
        }, Promise.resolve([] as Awaited<ReturnType<typeof EmployeeAuditLogApiService>>[]));

        batchResults.forEach((r) => {
          allEntries = allEntries.concat(r.results);
        });
      }

      const csv = generateAuditLogCSV(allEntries);
      const today = new Date().toISOString().slice(0, 10);
      downloadAuditLogCSV(csv, `employee-${employeeId}-audit-log-${today}.csv`);

      if (first.truncated) {
        showNotification({
          title: "Export may be incomplete",
          message:
            "Results were capped at 10,000 entries. Narrow the date range to export older records.",
          color: "yellow",
        });
      }
    } catch {
      showNotification({
        title: "Export failed",
        message: "Unable to download audit log. Please try again.",
        color: "red",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageContainer>
      <div
        className="flex items-center justify-between -mx-[40px] -mt-[32px] px-[40px] py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Audit log
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            View full employee change history.
          </p>
        </div>
        <div className="flex items-center gap-xxs">
          <Button
            variant="outline"
            leftIcon={<Download size={16} />}
            onClick={handleDownloadCsv}
            disabled={isExporting || isLoading || !data?.results.length}
          >
            {isExporting ? "Exporting..." : "Download CSV"}
          </Button>
        </div>
      </div>

      <Breadcrumb className="mb-md">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/employees">Employees</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{employeeName ?? "Employee"}</BreadcrumbPage>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Audit log</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <EmployeeAuditLogFilters
        filters={filters}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      {renderContent()}

      <Space h="lg" />

      <div className="inline-block">
        <Button variant="outline" onClick={() => navigate("/employees")}>
          Back to Employees
        </Button>
      </div>
    </PageContainer>
  );
};

export default EmployeeAuditLogPage;
